import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Building2, Loader2, Search, Save, ShieldCheck, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ROLES = ["diretor", "supervisao", "gerente", "vendedor", "operador", "financeiro", "oficina"];

const ROLE_COLOR = {
  diretor: "bg-purple-100 text-purple-700",
  supervisao: "bg-violet-100 text-violet-700",
  gerente: "bg-blue-100 text-blue-700",
  vendedor: "bg-sky-100 text-sky-700",
  operador: "bg-amber-100 text-amber-700",
  financeiro: "bg-emerald-100 text-emerald-700",
  oficina: "bg-orange-100 text-orange-700",
  cliente: "bg-slate-100 text-slate-700",
};

export default function AdminUnidadesUsuarios() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [q, setQ] = useState("");
  const [filterUnidade, setFilterUnidade] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ role: "", unidade_id: "", escopo: "" });
  const [saving, setSaving] = useState(false);

  const canEdit = user?.role === "diretor" && user?.escopo === "geral";

  const loadAll = async () => {
    setLoading(true);
    try {
      const [usersRes, unidadesRes] = await Promise.all([
        api.get("/admin/usuarios?limite=500"),
        api.get("/comercial/unidades"),
      ]);
      const u = Array.isArray(usersRes.data)
        ? usersRes.data
        : usersRes.data?.items || usersRes.data?.usuarios || [];
      setUsers(u);
      setUnidades(Array.isArray(unidadesRes.data) ? unidadesRes.data : []);
    } catch (e) {
      toast.error("Falha ao carregar usuários e unidades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const unidadeNome = (uid) => {
    if (!uid) return "—";
    const u = unidades.find((x) => x.id === uid);
    return u ? `${u.nome}/${u.estado || ""}${u.is_matriz ? " ★" : ""}` : uid;
  };

  const filtered = useMemo(() => {
    let arr = users;
    if (q) {
      const ql = q.toLowerCase();
      arr = arr.filter(
        (u) =>
          (u.email || "").toLowerCase().includes(ql) ||
          (u.nome || u.name || "").toLowerCase().includes(ql)
      );
    }
    if (filterUnidade) {
      arr = arr.filter((u) =>
        filterUnidade === "__none__" ? !u.unidade_id : u.unidade_id === filterUnidade
      );
    }
    if (filterRole) arr = arr.filter((u) => u.role === filterRole);
    return arr;
  }, [users, q, filterUnidade, filterRole]);

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      role: u.role || "",
      unidade_id: u.unidade_id || "",
      escopo: u.escopo || "",
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setForm({ role: "", unidade_id: "", escopo: "" });
  };

  const salvar = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      if (form.role !== editing.role) payload.role = form.role;
      if (form.unidade_id !== (editing.unidade_id || "")) payload.unidade_id = form.unidade_id || null;
      if (form.escopo !== (editing.escopo || "")) payload.escopo = form.escopo || "";
      if (Object.keys(payload).length === 0) {
        toast.info("Nada para alterar");
        closeEdit();
        return;
      }
      const { data } = await api.patch(`/admin/usuarios/${editing.id}/atribuicao`, payload);
      toast.success(`Atribuição salva — ${editing.email}`);
      // Atualiza local
      setUsers((arr) => arr.map((u) => (u.id === editing.id ? { ...u, ...data.user } : u)));
      closeEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="page-admin-unidades-usuarios">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Atribuição Unidades × Usuários</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visualize e ajuste rapidamente <strong>role</strong>, <strong>unidade</strong> e <strong>escopo</strong> dos usuários.
            Apenas Diretor Geral pode editar.
          </p>
        </div>
        <Button onClick={loadAll} variant="outline" size="sm" data-testid="adm-reload">
          {loading ? <Loader2 className="animate-spin" size={14} /> : "Recarregar"}
        </Button>
      </div>

      {!canEdit && (
        <Card className="p-3 bg-amber-50 border-amber-200 flex gap-2 items-center text-sm" data-testid="adm-readonly-banner">
          <AlertCircle size={16} className="text-amber-700" />
          <span>
            Modo somente leitura — apenas Diretor Geral (escopo = "geral") pode alterar atribuições.
          </span>
        </Card>
      )}

      <Card className="p-3 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="adm-busca"
            placeholder="Buscar por nome ou e-mail…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <select
          data-testid="adm-filtro-unidade"
          value={filterUnidade}
          onChange={(e) => setFilterUnidade(e.target.value)}
          className="h-9 px-3 text-sm border rounded-md bg-white"
        >
          <option value="">Todas unidades</option>
          <option value="__none__">Sem unidade</option>
          {unidades.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome}/{u.estado} {u.is_matriz ? "★" : ""}
            </option>
          ))}
        </select>
        <select
          data-testid="adm-filtro-role"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="h-9 px-3 text-sm border rounded-md bg-white"
        >
          <option value="">Todos perfis</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <Badge variant="outline" className="text-xs">
          {filtered.length} usuário{filtered.length === 1 ? "" : "s"}
        </Badge>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Perfil</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead className="w-20 text-center">Escopo</TableHead>
              <TableHead className="w-28 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <Loader2 className="inline animate-spin" size={14} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                  Nenhum usuário encontrado com esses filtros.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((u) => (
                <TableRow key={u.id} data-testid={`adm-user-${u.id}`}>
                  <TableCell className="font-medium">{u.nome || u.name || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={ROLE_COLOR[u.role] || "bg-slate-100"}>{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1">
                      <Building2 size={12} className="text-muted-foreground" />
                      {unidadeNome(u.unidade_id)}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {u.escopo === "geral" ? (
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">
                        <ShieldCheck size={10} className="mr-1" /> GERAL
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canEdit}
                      onClick={() => openEdit(u)}
                      data-testid={`adm-editar-${u.id}`}
                    >
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-w-md" data-testid="adm-modal-editar">
          <DialogHeader>
            <DialogTitle>Editar atribuição</DialogTitle>
            <DialogDescription>
              {editing?.email} — {editing?.nome || editing?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Perfil</Label>
              <select
                data-testid="adm-form-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
              >
                <option value="">— Selecione —</option>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Unidade</Label>
              <select
                data-testid="adm-form-unidade"
                value={form.unidade_id}
                onChange={(e) => setForm({ ...form, unidade_id: e.target.value })}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
              >
                <option value="">— Sem unidade (diretor geral) —</option>
                {unidades.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome}/{u.estado} {u.is_matriz ? "★ (matriz)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Escopo</Label>
              <select
                data-testid="adm-form-escopo"
                value={form.escopo}
                onChange={(e) => setForm({ ...form, escopo: e.target.value })}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
              >
                <option value="">— Normal —</option>
                <option value="geral">Geral (vê todas as unidades)</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">
                "Geral" só faz sentido para diretor, supervisão e operador (operador matriz).
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={closeEdit} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={salvar}
              disabled={saving}
              style={{ background: "#1B3A6B", color: "white" }}
              data-testid="adm-form-salvar"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save size={14} className="mr-2" />}
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
